import { useTranslation } from 'react-i18next';
import type { MethodContent, MethodContentSection, SwotStrategy } from '@/types/methodologies';

interface SwotViewProps {
  methodId: string;
  content: MethodContent;
}

const QUADRANTS: Array<{ key: string; bg: string }> = [
  { key: 'strengths', bg: 'bg-[#eaf7ee]' },
  { key: 'weaknesses', bg: 'bg-[#fdeceb]' },
  { key: 'opportunities', bg: 'bg-[rgba(33,151,241,0.08)]' },
  { key: 'threats', bg: 'bg-[#fdf3e3]' },
];

const STRATEGY_REF_LABEL_BG = 'bg-[#eeeeee]';

function Quadrant({ methodId, section, bg }: { methodId: string; section: MethodContentSection; bg: string }) {
  const { t } = useTranslation('methodologies');
  return (
    <div className={`${bg} rounded-[16px] p-[24px] flex flex-col gap-[12px]`}>
      <h3 className="font-manrope font-semibold text-[16px] leading-[24px] text-[#0d0d0d]">
        {t(`sections.${methodId}.${section.key}`, { defaultValue: section.title })}
      </h3>
      <ul className="flex flex-col gap-[8px]">
        {section.items.map((item, index) => (
          <li key={index} className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d] pl-[16px] relative">
            <span className="absolute left-0 top-[8px] w-[4px] h-[4px] rounded-full bg-[#0d0d0d]" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StrategyCard({ strategy }: { strategy: SwotStrategy }) {
  const { t } = useTranslation('methodologies');
  return (
    <div className="bg-[#f4f4f3] rounded-[16px] p-[24px] flex flex-col gap-[8px]">
      <h4 className="font-manrope font-semibold text-[16px] leading-[24px] text-[#0d0d0d]">
        {t(`swot.strategies.${strategy.key}.title`)}
      </h4>
      <p className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d]">
        {strategy.description}
      </p>
      <div className="flex flex-wrap gap-[8px] mt-[4px]">
        {strategy.refs.map((ref) => (
          <span
            key={ref}
            className={`${STRATEGY_REF_LABEL_BG} rounded-[8px] px-[8px] py-[4px] font-segoe text-[12px] leading-[16px] text-[#565858]`}
          >
            {t(`sections.swot.${ref}`, { defaultValue: ref })}
          </span>
        ))}
      </div>
    </div>
  );
}

/** SWOT: 2x2 matrix (S/W top, O/T bottom) plus the four required strategies. */
export function SwotView({ methodId, content }: SwotViewProps) {
  const { t } = useTranslation('methodologies');
  const byKey = new Map<string, MethodContentSection>(content.sections.map((s) => [s.key, s]));

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
        {QUADRANTS.map(({ key, bg }) => {
          const section = byKey.get(key);
          if (!section) return null;
          return <Quadrant key={key} methodId={methodId} section={section} bg={bg} />;
        })}
      </div>

      {content.strategies && content.strategies.length > 0 && (
        <div className="flex flex-col gap-[16px]">
          <h3 className="font-manrope font-bold text-[20px] leading-[28px] text-[#0d0d0d]">
            {t('swot.strategiesTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
            {content.strategies.map((strategy) => (
              <StrategyCard key={strategy.key} strategy={strategy} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
